import { useState, useCallback } from 'react';
import Linkify from 'react-linkify';
import { toast } from 'sonner';

/**
 * Custom hook for secure URL handling
 */
export const useSecureUrl = () => {
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [viewUrl, setViewUrl] = useState('');

  const isInternal = useCallback((url) => {
    try {
      const currentHostname = window.location.hostname;
      const urlObj = new URL(url, window.location.origin); // Resolve relative URLs
      return urlObj.hostname === currentHostname || url.startsWith('/');
    } catch {
      return url.startsWith('/'); // Assume relative paths are internal
    }
  }, []);

  const handleLinkClick = useCallback((url, event) => {
    try {
      if (isInternal(url)) {
        // For internal links, allow default navigation or use router if available
        // For now, direct navigation or rely on router to pick it up
        // If this is called from an <a> tag without preventDefault, the router will handle it.
        // If from JS, we can manually navigate.
        if (event) {
          // If called from an event, let the browser handle it (e.g., react-router)
          // Don't prevent default if it's an internal link
        } else {
          // If called directly without event, navigate programmatically
          window.location.href = url;
        }
        return;
      }

      const urlObj = new URL(url);
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        setViewUrl(url);
        setUrlModalOpen(true);
      } else {
        console.warn('Blocked insecure link:', url);
        toast.error('This link type is not allowed for security reasons');
      }
    } catch (e) {
      console.warn('Invalid URL:', url, e);
      toast.error('Invalid URL');
    }
  }, [isInternal]);

  const linkDecorator = useCallback((decoratedHref, decoratedText, key) => {
    const isLinkInternal = isInternal(decoratedHref);

    const handleClick = (e) => {
      if (!isLinkInternal) {
        e.preventDefault();
        e.stopPropagation();
        handleLinkClick(decoratedHref, e);
      }
      // For internal links, do not prevent default, let the browser/router handle it
    };

    return (
      <a
        key={key}
        href={decoratedHref}
        onClick={handleClick}
        className="text-blue-400 hover:text-blue-300 underline break-all cursor-pointer"
        rel={isLinkInternal ? "" : "noopener noreferrer"} // Only add rel for external links
        target={isLinkInternal ? "" : "_blank"} // Only open in new tab for external links
        title={`Open: ${decoratedHref}`}
      >
        {decoratedText}
      </a>
    );
  }, [handleLinkClick, isInternal]);

  return {
    urlModalOpen,
    setUrlModalOpen,
    viewUrl,
    handleLinkClick,
    linkDecorator
  };
};

export default useSecureUrl;
